import { getRequest, patchRequest, postRequest, putRequest } from "@/lib/api";

export type OrderStatus =
  | "AWAITING_DEPOSIT"
  | "CONTRACT_PENDING"
  | "AWAITING_BALANCE"
  | "HANDOVER_PENDING"
  | "BUYER_CONFIRMATION_PENDING"
  | "COMPLETED"
  | "CANCELLED";

export type ContractStatus = "NOT_READY" | "READY_FOR_SIGNATURE" | "SIGNED" | "VOIDED";
export type HandoverStatus = "NOT_READY" | "READY" | "HANDED_OVER" | "BUYER_CONFIRMED";
export type PaymentKind = "DEPOSIT" | "BALANCE";

export type OrderRecord = {
  id: string;
  applicationId: string;
  buyerId: string;
  breederProfileId: string;
  dogId: number;
  agreedPrice: number;
  depositRateBps: number;
  status: OrderStatus;
  contractStatus: ContractStatus;
  handoverStatus: HandoverStatus;
  timeline: { status: OrderStatus; occurredAt: string; actorId: string; note: string }[];
  createdAt: string;
  updatedAt: string;
  sellerCompletedAt: string | null;
  buyerConfirmedAt: string | null;
  depositPaidAt:string|null;
  refundStatus:"NONE"|"REQUESTED"|"REFUNDED"|"REJECTED";
  refundRequestedAt:string|null;
  refundReason:string|null;
};

export type PaymentTransaction = {
  id: string;
  providerTransactionId: string | null;
  orderId: string;
  kind: PaymentKind;
  amount: number;
  providerOrderId: string;
  status: "READY" | "CONFIRMING" | "DONE" | "FAILED";
  method: string | null;
  approvedAt: string | null;
  createdAt: string;
  virtualAccount: VirtualAccount | null;
};

export type VirtualAccount = {
  account: string;
  bankName: string;
  bankCode: string;
  depositor: string;
  expiresAt: string;
};

export type Checkout = {
  transactionId: string;
  providerTransactionId: string | null;
  providerOrderId: string;
  amount: number;
  currency: "KRW";
  kind: PaymentKind;
  provider: "KCP_ESCROW";
  available: boolean;
  form: Record<string, string> | null;
  method: "BANK" | "VCNT";
  virtualAccount: VirtualAccount | null;
};

export const getOrders = () => getRequest<OrderRecord[]>("orders");
export const getOrder = (id: string) => getRequest<OrderRecord>(`orders/${id}`);
export const getBreederOrders=()=>getRequest<OrderRecord[]>("breeders/me/orders");
export type HandoverItems={identityChecked:boolean;healthRecordsPrepared:boolean;contractCopyPrepared:boolean;suppliesPrepared:boolean;transportPlanConfirmed:boolean;conditionRecorded:boolean};
export type HandoverDocument={id:string;orderId:string;originalName:string;mimeType:string;size:number;sha256:string;createdAt:string};
export type HandoverPackage={checklist:{id:string;orderId:string;items:HandoverItems;note:string|null;updatedAt:string}|null;documents:HandoverDocument[]};
export const getHandoverPackage=(orderId:string)=>getRequest<HandoverPackage>(`orders/${orderId}/handover-package`);
export const updateHandoverChecklist=(orderId:string,input:HandoverItems&{note?:string})=>putRequest<object,typeof input>(`breeders/me/orders/${orderId}/handover-checklist`,input);
export const uploadHandoverDocument=(orderId:string,file:File)=>{const data=new FormData();data.append("file",file);return postRequest<HandoverDocument,FormData>(`breeders/me/orders/${orderId}/handover-documents`,data)};
export const getPayments = (orderId: string) => getRequest<PaymentTransaction[]>(`orders/${orderId}/payments`);
export const createCheckout = (orderId: string, kind: PaymentKind, method: "BANK" | "VCNT") =>
  postRequest<Checkout, { method: "BANK" | "VCNT" }>(`orders/${orderId}/payments/${kind.toLowerCase()}/checkout`, { method });
export const confirmCheckout = (
  orderId: string,
  transactionId: string,
  input: { encData: string; encInfo: string; tranCd: string; providerOrderId: string; amount: number },
) => postRequest<PaymentTransaction, typeof input>(`orders/${orderId}/payments/${transactionId}/confirm`, input);
export const createBuyerDepositOrder=(applicationId:string)=>postRequest<OrderRecord,object>(`applications/${applicationId}/deposit-order`,{});
export const createBuyerFullOrder=(applicationId:string)=>postRequest<OrderRecord,object>(`applications/${applicationId}/full-order`,{});
export const requestSellerPayment=async(applicationId:string,kind:PaymentKind)=>{const order=await postRequest<OrderRecord,object>(`breeders/me/applications/${applicationId}/payment-request/${kind}`,{});await postRequest<object,{body:string}>(`applications/${applicationId}/messages`,{body:kind==="DEPOSIT"?`보증금 결제를 요청했습니다. 주문 #${order.id.slice(0,8)}에서 결제해주세요.`:`전체 금액 결제를 요청했습니다. 이미 낸 보증금은 자동으로 차감됩니다. 주문 #${order.id.slice(0,8)}에서 결제해주세요.`});return order};
export const requestBuyerRefund=(orderId:string,reason:string)=>postRequest<OrderRecord,{reason:string}>(`orders/${orderId}/payments/refund`,{reason});
export const decideSellerRefund=(orderId:string,approved:boolean,reason:string)=>patchRequest<OrderRecord,{approved:boolean;reason:string}>(`orders/${orderId}/payments/refund/seller-decision`,{approved,reason});
export const cancelSellerReservation=(orderId:string,reason:string)=>postRequest<OrderRecord,{reason:string}>(`orders/${orderId}/payments/seller-cancel`,{reason});
export const updateBreederHandover=(orderId:string,status:"READY"|"HANDED_OVER")=>patchRequest<OrderRecord,{status:string}>(`breeders/me/orders/${orderId}/handover`,{status});
export const confirmBuyerHandover=(orderId:string)=>postRequest<OrderRecord,object>(`orders/${orderId}/settlement/confirm-handover`,{});
export type DisputeRecord={id:string;orderId:string;type:"TRANSACTION"|"HANDOVER"|"HEALTH"|"WELFARE"|"OTHER";status:"OPEN"|"UNDER_REVIEW"|"RESOLVED"|"REJECTED"|"WITHDRAWN";subject:string;description:string;blocksSettlement:boolean;createdAt:string};
export const getOrderDisputes=(orderId:string)=>getRequest<DisputeRecord[]>(`orders/${orderId}/disputes`);
export const createOrderDispute=(orderId:string,input:{type:DisputeRecord["type"];subject:string;description:string})=>postRequest<DisputeRecord,typeof input>(`orders/${orderId}/disputes`,input);
export type SettlementView={settlement:null|{id:string;status:string;platformFee:number;payoutAmount:number;releaseAt:string;releaseReason:string|null;failureReason:string|null};release:null|{status:string;availableAt:string;reason:string;attempts:number;lastError:string|null};payoutMode:"TEST"|"LIVE"};
export const getOrderSettlement=(orderId:string)=>getRequest<SettlementView>(`orders/${orderId}/settlement`);
